import { MUICheckBox, MUIChip, MUIDateTimePicker } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import dayjs, { Dayjs } from "dayjs"
import { MouseEvent, useCallback, useContext, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm, useWatch } from "react-hook-form"
import { Navigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../../components/DiMaProgress"
import { UserContext } from "../../../../../context/UserContext"
import {
  ApplicationPermissionUserGet,
  ApplicationPermissionUserPut,
} from "../../../../../models/ApplicationPermissionUser"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../../models/Authorization"
import {
  getLocalDateTime,
  getNiceFormattedDate,
  getUtcDateTime,
} from "../../../../../utils/getFormattedDate"
import { getUserApplicationStatusProps } from "../../../../../utils/getUserApplicationStatusProps"

const validationSchema = z.object({
  validityStartDate: z.string().min(1, "Validity Start Date is required"),
  validityEndDate: z.string().min(1, "Validity End Date is required"),
  isDisabled: z.boolean({ error: "Is Disabled is required" }),
})

const MAX_DATE = dayjs("9999-12-31T23:59:59")

export function UserApplicationPermissionDetails(props: {
  readonly userApplicationPermission: ApplicationPermissionUserGet
  readonly setUserApplicationPermission: (
    updatedUserApplicationPermission: ApplicationPermissionUserGet
  ) => void
}) {
  const { userApplicationPermission, setUserApplicationPermission } = props
  const { userId, applicationId, permissionId } = useParams()
  const { user, isLoadingUser } = useContext(UserContext)
  const returnURL = `/users/${userId}#apps-permissions`

  const formMethods = useForm<ApplicationPermissionUserPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      validityStartDate: getLocalDateTime(userApplicationPermission.validityStartDate),
      validityEndDate: getLocalDateTime(userApplicationPermission.validityEndDate),
      isDisabled: userApplicationPermission.isDisabled || false,
    })
  }, [userApplicationPermission, reset])

  const watchedStartDate = useWatch({
    control,
    name: "validityStartDate",
  })

  const onSubmit = useCallback(
    async (values: ApplicationPermissionUserPut) => {
      const utcValidityStartDate = getUtcDateTime(values.validityStartDate)
      const utcValidityEndDate = getUtcDateTime(values.validityEndDate)

      try {
        const res = await digitalManagerApi.put<ApplicationPermissionUserGet>(
          `/users/${userId}/applications/${applicationId}/permissions/${permissionId}`,
          {
            validityStartDate: utcValidityStartDate,
            validityEndDate: utcValidityEndDate,
            isDisabled: values.isDisabled,
          }
        )
        toast.success("User application permission updated")
        setUserApplicationPermission(res.data)
      } catch {
        toast.error("Cannot update user application permission")
      }
    },
    [userId, applicationId, permissionId, setUserApplicationPermission]
  )

  const handleDateChangeValidityStartDate = useCallback(
    (field: ControllerRenderProps<ApplicationPermissionUserPut, "validityStartDate">) =>
      (date: Dayjs | null) => {
        field.onChange(date?.toISOString() ?? "")
      },
    []
  )

  const handleValidityStartDatePickerChange = useCallback(
    (field: ControllerRenderProps<ApplicationPermissionUserPut, "validityStartDate">) =>
      (date: Dayjs | null) => {
        if (date?.isAfter(MAX_DATE)) {
          setValue("validityStartDate", MAX_DATE.toISOString(), {
            shouldValidate: true,
          })
          return
        }
        handleDateChangeValidityStartDate(field)(date)
      },
    [handleDateChangeValidityStartDate, setValue]
  )

  const renderDateTimePickerValidityStartDate = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationPermissionUserPut, "validityStartDate">
    }) => (
      <MUIDateTimePicker
        id="validityStartDate"
        name="validityStartDate"
        size={40}
        value={field.value ? dayjs(field.value) : null}
        isDisabled={false}
        onChange={handleValidityStartDatePickerChange(field)}
        errors={
          errors.validityStartDate
            ? { validityStartDate: { message: String(errors.validityStartDate.message) } }
            : undefined
        }
      />
    ),
    [errors.validityStartDate, handleValidityStartDatePickerChange]
  )

  const handleDateChangeValidityEndDate = useCallback(
    (field: ControllerRenderProps<ApplicationPermissionUserPut, "validityEndDate">) =>
      (date: Dayjs | null) => {
        field.onChange(date?.toISOString() ?? "")
      },
    []
  )

  const handleValidityEndDatePickerChange = useCallback(
    (field: ControllerRenderProps<ApplicationPermissionUserPut, "validityEndDate">) =>
      (date: Dayjs | null) => {
        const start = watchedStartDate ? dayjs(watchedStartDate) : null

        if (date && start && date.isBefore(start)) {
          setValue("validityEndDate", "", { shouldValidate: true })
          return
        }

        if (date?.isAfter(MAX_DATE)) {
          setValue("validityEndDate", MAX_DATE.toISOString(), { shouldValidate: true })
          return
        }

        handleDateChangeValidityEndDate(field)(date)
      },
    [watchedStartDate, setValue, handleDateChangeValidityEndDate]
  )

  const renderDateTimePickerValidityEndDate = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationPermissionUserPut, "validityEndDate">
    }) => (
      <MUIDateTimePicker
        id="validityEndDate"
        name="validityEndDate"
        size={40}
        value={field.value ? dayjs(field.value) : null}
        isDisabled={false}
        onChange={handleValidityEndDatePickerChange(field)}
        errors={
          errors.validityEndDate
            ? { validityEndDate: { message: String(errors.validityEndDate.message) } }
            : undefined
        }
      />
    ),
    [errors.validityEndDate, handleValidityEndDatePickerChange]
  )

  const handleIsDisabledToggle = useCallback(
    (field: ControllerRenderProps<ApplicationPermissionUserPut, "isDisabled">) =>
      (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderCheckboxIsDisabled = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<ApplicationPermissionUserPut, "isDisabled">
    }) => (
      <MUICheckBox
        {...field}
        checked={field.value}
        handleToggle={handleIsDisabledToggle(field)}
      />
    ),
    [handleIsDisabledToggle]
  )

  if (!userApplicationPermission || !applicationId) {
    return <Navigate to={returnURL} />
  }

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const canWrite = hasUserAccessToApplicationFeature(
    user.authorizations.applications,
    applicationId,
    DimaFeatureShortNameEnum.RM,
    AccessLevel.Write
  )

  const detailsContentProps: DiMaDetailsContentProps<ApplicationPermissionUserPut> = {
    canWrite,
    formMethods,
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Status",
        displayItem: (
          <MUIChip {...getUserApplicationStatusProps(userApplicationPermission.status)} />
        ),
      },
      {
        id: 1,
        label: "Validity Start Date",
        displayItem: (
          <div>{getNiceFormattedDate(userApplicationPermission.validityStartDate)}</div>
        ),
        editItem: (
          <Controller
            name="validityStartDate"
            control={control}
            render={renderDateTimePickerValidityStartDate}
          />
        ),
      },
      {
        id: 2,
        label: "Validity End Date",
        displayItem: (
          <div>{getNiceFormattedDate(userApplicationPermission.validityEndDate)}</div>
        ),
        editItem: (
          <Controller
            name="validityEndDate"
            control={control}
            render={renderDateTimePickerValidityEndDate}
          />
        ),
      },
      {
        id: 3,
        label: "Is Disabled",
        displayItem: (
          <div>{userApplicationPermission.isDisabled?.toString() || "false"}</div>
        ),
        editItem: (
          <Controller
            name="isDisabled"
            control={control}
            render={renderCheckboxIsDisabled}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
